// entry.ts — the landing / join overlay: pick a name, then create or join a room.
//
// Doubling as the audio-unlock gesture: the "enter" button click is a real user
// gesture, so the AudioContext can be resumed right when the player walks in.

import { randomName, randomRoomId, sanitizeName } from "../identity";

export interface EntryResult {
  name: string;
  roomId: string;
}

export function mountEntry(
  root: HTMLElement,
  opts: { roomId: string | null; suggestedName: string; onEnter: (r: EntryResult) => void },
): void {
  root.innerHTML = "";
  root.hidden = false;

  const card = document.createElement("div");
  card.className = "entry-card";

  const title = document.createElement("h1");
  title.className = "entry-title";
  title.textContent = "don't look at me";

  const tagline = document.createElement("p");
  tagline.className = "entry-tagline";
  tagline.textContent = opts.roomId
    ? `joining room · ${opts.roomId}`
    : "a shared canvas you play together — draw notes, hear everyone";

  const nameInput = document.createElement("input");
  nameInput.className = "entry-input";
  nameInput.type = "text";
  nameInput.maxLength = 24;
  nameInput.placeholder = "your name";
  nameInput.value = opts.suggestedName;
  nameInput.autocomplete = "off";
  nameInput.spellcheck = false;
  nameInput.setAttribute("aria-label", "your name");

  const actions = document.createElement("div");
  actions.className = "entry-actions";

  function enter(roomId: string): void {
    const name = sanitizeName(nameInput.value) || randomName();
    opts.onEnter({ name, roomId });
  }

  const primary = document.createElement("button");
  primary.className = "entry-btn primary";
  primary.textContent = opts.roomId ? "enter" : "create a room";
  primary.addEventListener("click", () => enter(opts.roomId ?? randomRoomId()));
  actions.appendChild(primary);

  card.appendChild(title);
  card.appendChild(tagline);
  card.appendChild(nameInput);

  // On the lobby (no room in the URL) offer a join-by-code field too.
  if (!opts.roomId) {
    const joinRow = document.createElement("div");
    joinRow.className = "entry-join";

    const codeInput = document.createElement("input");
    codeInput.className = "entry-input";
    codeInput.type = "text";
    codeInput.placeholder = "or enter a room code";
    codeInput.autocomplete = "off";
    codeInput.spellcheck = false;

    const joinBtn = document.createElement("button");
    joinBtn.className = "entry-btn";
    joinBtn.textContent = "join";
    function tryJoin(): void {
      const code = codeInput.value.replace(/\s+/g, "").toLowerCase();
      if (code) enter(code);
    }
    joinBtn.addEventListener("click", tryJoin);
    codeInput.addEventListener("keydown", (e) => { if (e.key === "Enter") tryJoin(); });

    joinRow.appendChild(codeInput);
    joinRow.appendChild(joinBtn);
    card.appendChild(actions);
    card.appendChild(joinRow);
  } else {
    nameInput.addEventListener("keydown", (e) => { if (e.key === "Enter") enter(opts.roomId as string); });
    card.appendChild(actions);
  }

  root.appendChild(card);
  setTimeout(() => nameInput.focus(), 50);
}
